import React, {useState} from 'react';

import {CheckBox} from '@pb/kit-component-checkbox';
import {Group} from '@pb/kit-component-group';
import Loading, {Types as LoadingTypes} from '@pb/kit-component-loading';
import {PaginationStripe} from '@pb/kit-component-pagination';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import Typography from '@pb/kit-component-typography';

import {ComparisonSection} from './comparison-section';
import {DEFAULT_PAGE_SIZE, SECTIONS} from './constants';
import {resolveColumns} from './utils';

import {
    migrationStepperAcknowledgeBox,
} from './migration-stepper.module.styl';

import type {ComparisonRow} from './comparison-section';
import type {MigrationPreview} from 'src/openapi/admin/models/MigrationPreview';
import type {UserContentMigrationState} from 'src/openapi/admin/models/UserContentMigrationState';

type Props = {
    preview: MigrationPreview | null,
    previewLoading: boolean,
    acknowledged: boolean,
    onAcknowledgedChange: (value: boolean) => void,
    hasTemplateUser: boolean,
    hasContentSelected: boolean,
    currentUserContent: UserContentMigrationState | null,
};

export const ConfirmStep = (props: Props) => {
    const {
        preview,
        previewLoading,
        acknowledged,
        onAcknowledgedChange,
        hasTemplateUser,
        hasContentSelected,
        currentUserContent,
    } = props;

    const [{currentPage}, setCurrentPage] = useState<{currentPage: number}>(
        {currentPage: 1},
    );

    const resolvedSections = SECTIONS.map(section => {
        const {current, resulting} = resolveColumns({
            section: section.key,
            preview,
            hasTemplateUser,
            hasContentSelected,
            currentUserContent,
        });
        const maxLen = Math.max(current.length, resulting.length);
        const rows: ComparisonRow[] = Array.from({length: maxLen}).map(
            (_, i) => ({
                left: current[i]?.name ?? '',
                right: resulting[i]?.name ?? '',
            }),
        );
        return {section, rows};
    });

    const totalRows = resolvedSections.reduce(
        (sum, {rows}) => sum + Math.max(rows.length, 1),
        0,
    );

    const startIdx = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const endIdx = Math.min(startIdx + DEFAULT_PAGE_SIZE, totalRows);

    let cumulativeIdx = 0;
    const sectionRanges = resolvedSections.map(({rows}) => {
        const size = Math.max(rows.length, 1);
        const range = {start: cumulativeIdx, end: cumulativeIdx + size};
        cumulativeIdx += size;
        return range;
    });

    return (
        <Group direction="v" style={{padding: 24, gap: 16}}>

            {previewLoading ? (
                <Group justify="center" style={{padding: 24}}>
                    <Loading type={LoadingTypes.ICON}/>
                </Group>
            ) : (
                <>
                    <Table dataTableRows={true} fixed={false}>
                        <Head>
                            <Row>
                                <Cell as="th">
                                    <Typography fontWeight="semi-bold">
                                        Current Content
                                    </Typography>
                                </Cell>
                                <Cell as="th">
                                    <Typography fontWeight="semi-bold">
                                        Resulting Content
                                    </Typography>
                                </Cell>
                            </Row>
                        </Head>
                        <Body>
                            {resolvedSections.map((
                                {section, rows},
                                idx,
                            ) => {
                                const range = sectionRanges[idx];
                                const sectionStartIdx = Math.max(
                                    startIdx,
                                    range.start,
                                );
                                const sectionEndIdx = Math.min(
                                    endIdx,
                                    range.end,
                                );
                                return (
                                    <ComparisonSection
                                        key={section.key}
                                        title={section.title}
                                        rows={rows}
                                        startIdx={
                                            sectionStartIdx - range.start
                                        }
                                        endIdx={
                                            sectionEndIdx - range.start
                                        }
                                    />
                                );
                            })}
                        </Body>
                    </Table>
                    {totalRows > DEFAULT_PAGE_SIZE && (
                        <PaginationStripe
                            totalCount={totalRows}
                            currentPage={currentPage}
                            pageSize={DEFAULT_PAGE_SIZE}
                            showSizeSwitcher={false}
                            goToPage={true}
                            onPaginationChange={p => setCurrentPage({currentPage: p.currentPage})}
                        />
                    )}
                </>
            )}

            <Group className={migrationStepperAcknowledgeBox}>
                <CheckBox
                    label="I understand this action cannot be undone for transferred items."
                    checked={acknowledged}
                    onCheckBoxChange={e => {
                        onAcknowledgedChange(
                            e.event.currentTarget.checked,
                        );
                    }}
                    name="acknowledge-migration"
                />
            </Group>
        </Group>
    );
};
